import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { companyApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Save, Loader2, Upload, Palette, X, Image as ImageIcon, Hash, MessageSquare, Phone, Plus, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIMEN_FISCAL = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
  { value: "612", label: "612 - Personas Físicas con Actividades Empresariales" },
  { value: "621", label: "621 - Incorporación Fiscal" },
  { value: "625", label: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "626 - Régimen Simplificado de Confianza" }
];

const SettingsPage = () => {
  const { user, fetchUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [hasCompany, setHasCompany] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [savingFolios, setSavingFolios] = useState(false);
  const [newWhatsAppNumber, setNewWhatsAppNumber] = useState("");
  const [addingNumber, setAddingNumber] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    rfc: "",
    razon_social: "",
    regimen_fiscal: "",
    codigo_postal: "",
    email: "",
    phone: "",
    logo_url: ""
  });

  const [folioConfig, setFolioConfig] = useState({
    invoice: { series: "F", next_folio: 1 },
    credit_note: { series: "NC", next_folio: 1 },
    payment_complement: { series: "P", next_folio: 1 }
  });

  const [whatsappNumbers, setWhatsappNumbers] = useState([]);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      const response = await companyApi.getMyCompany();
      setFormData({
        name: response.data.name || "",
        rfc: response.data.rfc || "",
        razon_social: response.data.razon_social || "",
        regimen_fiscal: response.data.regimen_fiscal || "",
        codigo_postal: response.data.codigo_postal || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        logo_url: response.data.logo_url || ""
      });
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      if (response.data.folio_config) {
        setFolioConfig(response.data.folio_config);
      }
      if (response.data.whatsapp_numbers) {
        setWhatsappNumbers(response.data.whatsapp_numbers);
      }
      setHasCompany(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setHasCompany(false);
        setIsCreating(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten imágenes (JPG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no debe superar 2MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target?.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploadingLogo(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.post(`${API}/upload/logo`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success("Logo subido exitosamente");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error al subir el logo");
      // Revert preview if upload failed
      setLogoPreview(formData.logo_url || null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveFolios = async () => {
    setSavingFolios(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.put(`${API}/companies/folio-config`, {
        invoice_series: folioConfig.invoice.series,
        invoice_start: folioConfig.invoice.next_folio,
        credit_note_series: folioConfig.credit_note.series,
        credit_note_start: folioConfig.credit_note.next_folio,
        payment_complement_series: folioConfig.payment_complement.series,
        payment_complement_start: folioConfig.payment_complement.next_folio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configuración de folios guardada");
    } catch (error) {
      toast.error("Error al guardar la configuración de folios");
    } finally {
      setSavingFolios(false);
    }
  };

  const handleAddWhatsAppNumber = async () => {
    if (!newWhatsAppNumber || newWhatsAppNumber.length < 10) {
      toast.error("Ingresa un número de teléfono válido");
      return;
    }
    
    setAddingNumber(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/companies/whatsapp-numbers`, {
        phone: newWhatsAppNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappNumbers([...whatsappNumbers, newWhatsAppNumber]);
      setNewWhatsAppNumber("");
      toast.success("Número agregado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar el número");
    } finally {
      setAddingNumber(false);
    }
  };

  const handleRemoveWhatsAppNumber = async (phone) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/companies/whatsapp-numbers/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappNumbers(whatsappNumbers.filter(n => n !== phone));
      toast.success("Número eliminado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el número");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rfc || formData.rfc.length < 12) {
      toast.error("RFC inválido (mínimo 12 caracteres)");
      return;
    }

    if (!formData.codigo_postal || formData.codigo_postal.length !== 5) {
      toast.error("Código postal inválido (5 dígitos)");
      return;
    }

    setSaving(true);

    try {
      if (isCreating) {
        await companyApi.createCompany(formData);
        toast.success("Empresa registrada exitosamente");
        setIsCreating(false);
        setHasCompany(true);
        await fetchUser();
      } else {
        await companyApi.updateCompany(formData);
        toast.success("Datos actualizados");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isCreating ? "Configurar Empresa" : "Configuración"}
        </h1>
        <p className="text-slate-600">
          {isCreating 
            ? "Registra los datos fiscales de tu empresa para comenzar a facturar"
            : "Administra los datos de tu empresa"}
        </p>
      </div>

      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#5B9BD5]" />
            Datos Fiscales
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo Upload Section */}
            <div className="space-y-2">
              <Label>Logo de la Empresa</Label>
              <div className="flex items-start gap-4">
                <div 
                  className={`relative w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden transition-colors ${
                    logoPreview ? 'border-[#5B9BD5] bg-slate-50' : 'border-slate-300 hover:border-[#5B9BD5]'
                  }`}
                >
                  {logoPreview ? (
                    <>
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-full h-full object-contain p-2"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center p-4">
                      <ImageIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <span className="text-xs text-slate-500">Sin logo</span>
                    </div>
                  )}
                  {uploadingLogo && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#5B9BD5]" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="mb-2"
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Logo
                  </Button>
                  <p className="text-xs text-slate-500">
                    Formatos: JPG, PNG, GIF, WEBP<br />
                    Tamaño máximo: 2MB<br />
                    Recomendado: 200x200px
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre de la Empresa</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mi Empresa S.A. de C.V."
                  className="cia-input"
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>RFC</Label>
                <Input
                  value={formData.rfc}
                  onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                  placeholder="XAXX010101000"
                  className="cia-input font-mono"
                  required
                  maxLength={13}
                  disabled={hasCompany && !isCreating}
                  data-testid="company-rfc-input"
                />
                {hasCompany && !isCreating && (
                  <p className="text-xs text-slate-500">El RFC no se puede modificar</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Razón Social</Label>
              <Input
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                placeholder="Razón social como aparece en la Constancia de Situación Fiscal"
                className="cia-input"
                required
                data-testid="company-razon-social-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Régimen Fiscal</Label>
                <Select 
                  value={formData.regimen_fiscal} 
                  onValueChange={(value) => setFormData({ ...formData, regimen_fiscal: value })}
                >
                  <SelectTrigger data-testid="company-regimen-select">
                    <SelectValue placeholder="Seleccionar régimen" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIMEN_FISCAL.map((regimen) => (
                      <SelectItem key={regimen.value} value={regimen.value}>{regimen.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Código Postal</Label>
                <Input
                  value={formData.codigo_postal}
                  onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                  placeholder="00000"
                  className="cia-input"
                  required
                  maxLength={5}
                  data-testid="company-cp-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Correo Electrónico</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="facturacion@miempresa.com"
                  className="cia-input"
                  required
                  data-testid="company-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Principal *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="cia-input"
                  required
                  data-testid="company-phone-input"
                />
                <p className="text-xs text-slate-500">Este número se usará para facturar por WhatsApp</p>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-3">
              <Button 
                type="submit" 
                className="cia-btn-primary" 
                disabled={saving}
                data-testid="save-company-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    {isCreating ? "Registrar Empresa" : "Guardar Cambios"}
                  </>
                )}
              </Button>
              
              {hasCompany && (
                <Link to="/dashboard/pdf-customizer">
                  <Button type="button" variant="outline">
                    <Palette className="w-5 h-5 mr-2" />
                    Personalizar PDF
                  </Button>
                </Link>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Info about Facturama */}
      <Card className="cia-card border-blue-200 bg-blue-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-blue-800 mb-2">Conexión con el SAT</h3>
          <p className="text-blue-700 text-sm">
            Para emitir CFDI válidos ante el SAT, necesitarás cargar tus archivos CSD (.cer y .key) 
            y conectar con un PAC autorizado. Contacta a soporte para configurar tu conexión con Facturama.
          </p>
        </CardContent>
      </Card>

      {/* Folio Configuration */}
      {hasCompany && (
        <Card className="cia-card" data-testid="folio-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <Hash className="w-5 h-5 text-[#5B9BD5]" />
              Configuración de Folios y Series
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-slate-600">
              Personaliza las series y folios iniciales para tus documentos fiscales. 
              Por ejemplo: F-001, FAC001, A-0001, etc.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Invoices */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-slate-800">Facturas (Ingreso)</h4>
                <div className="space-y-2">
                  <Label className="text-sm">Serie</Label>
                  <Input
                    value={folioConfig.invoice?.series || "F"}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      invoice: { ...folioConfig.invoice, series: e.target.value.toUpperCase() }
                    })}
                    placeholder="F"
                    className="cia-input"
                    maxLength={10}
                    data-testid="invoice-series-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Folio Inicial</Label>
                  <Input
                    type="number"
                    value={folioConfig.invoice?.next_folio || 1}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      invoice: { ...folioConfig.invoice, next_folio: parseInt(e.target.value) || 1 }
                    })}
                    min={1}
                    className="cia-input"
                    data-testid="invoice-start-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Preview: {folioConfig.invoice?.series || "F"}-{String(folioConfig.invoice?.next_folio || 1).padStart(6, '0')}
                </p>
              </div>

              {/* Credit Notes */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-slate-800">Notas de Crédito</h4>
                <div className="space-y-2">
                  <Label className="text-sm">Serie</Label>
                  <Input
                    value={folioConfig.credit_note?.series || "NC"}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      credit_note: { ...folioConfig.credit_note, series: e.target.value.toUpperCase() }
                    })}
                    placeholder="NC"
                    className="cia-input"
                    maxLength={10}
                    data-testid="credit-note-series-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Folio Inicial</Label>
                  <Input
                    type="number"
                    value={folioConfig.credit_note?.next_folio || 1}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      credit_note: { ...folioConfig.credit_note, next_folio: parseInt(e.target.value) || 1 }
                    })}
                    min={1}
                    className="cia-input"
                    data-testid="credit-note-start-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Preview: {folioConfig.credit_note?.series || "NC"}-{String(folioConfig.credit_note?.next_folio || 1).padStart(6, '0')}
                </p>
              </div>

              {/* Payment Complements */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-slate-800">Complementos de Pago</h4>
                <div className="space-y-2">
                  <Label className="text-sm">Serie</Label>
                  <Input
                    value={folioConfig.payment_complement?.series || "P"}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      payment_complement: { ...folioConfig.payment_complement, series: e.target.value.toUpperCase() }
                    })}
                    placeholder="P"
                    className="cia-input"
                    maxLength={10}
                    data-testid="payment-series-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Folio Inicial</Label>
                  <Input
                    type="number"
                    value={folioConfig.payment_complement?.next_folio || 1}
                    onChange={(e) => setFolioConfig({
                      ...folioConfig,
                      payment_complement: { ...folioConfig.payment_complement, next_folio: parseInt(e.target.value) || 1 }
                    })}
                    min={1}
                    className="cia-input"
                    data-testid="payment-start-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Preview: {folioConfig.payment_complement?.series || "P"}-{String(folioConfig.payment_complement?.next_folio || 1).padStart(6, '0')}
                </p>
              </div>
            </div>

            <Button 
              onClick={handleSaveFolios}
              className="cia-btn-primary"
              disabled={savingFolios}
              data-testid="save-folios-btn"
            >
              {savingFolios ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Configuración de Folios
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Numbers Management */}
      {hasCompany && (
        <Card className="cia-card" data-testid="whatsapp-numbers-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Números de WhatsApp Autorizados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Estos números están autorizados para emitir facturas por WhatsApp. 
              Cuando alguien envíe un mensaje desde uno de estos números, el sistema sabrá que la factura es de tu empresa.
            </p>

            {/* Current numbers */}
            <div className="space-y-2">
              {whatsappNumbers.map((phone, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{phone}</span>
                    {index === 0 && (
                      <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">Principal</span>
                    )}
                  </div>
                  {whatsappNumbers.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveWhatsAppNumber(phone)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-whatsapp-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Add new number */}
            <div className="flex gap-2">
              <Input
                value={newWhatsAppNumber}
                onChange={(e) => setNewWhatsAppNumber(e.target.value)}
                placeholder="+52 55 1234 5678"
                className="cia-input"
                data-testid="new-whatsapp-input"
              />
              <Button
                onClick={handleAddWhatsAppNumber}
                disabled={addingNumber}
                variant="outline"
                data-testid="add-whatsapp-btn"
              >
                {addingNumber ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              El número de teléfonos permitidos depende de tu plan:
              Básico (1), Profesional (5), Empresarial (ilimitados)
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
